import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Pagination } from '../../components/ui/pagination';
import {
  Plus,
  Folder,
  FolderPlus,
  Download,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  FolderOpen,
  User,
  Building2,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  Archive,
} from 'lucide-react';
import { Document, CategoryWithDocuments } from '../../lib/api';
import { getFileIcon, getCategoryColor } from './utils';
import {
  FolderTile,
  RenameFolderDialog,
  DeleteFolderDialog,
  CreateSubfolderDialog,
} from './FolderTile';
import { FolderAccessDialog } from './FolderAccessDialog';
import { DocumentRowList, DocumentSortHeader, useDocumentSort } from './DocumentRows';


// Client-side pagination page-size for the drilled-in doc list.
const DOCS_PER_PAGE = 24;

type SubLayout = 'tiles' | 'list';

// Larger page-size for the polished tile grid — tiles are compact so we
// can show a full grid before paging kicks in.
const FOLDER_GRID_PAGE_SIZE = 12;

interface FolderViewProps {
  categories: CategoryWithDocuments[];
  searchTerm: string;
  onDownload: (doc: Document) => void;
  // Optional preset folder id pre-selects the upload target so an "Upload
  // here" action from inside a drilled-in folder lands in that folder.
  onUploadOpen: (presetCategoryId?: number) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  // CR-5330: Open the document details popup. When provided, clicking a
  // tile/row body opens the modal; clicking inline buttons or the kebab
  // menu still triggers their direct action (those handlers stop event
  // propagation so a row click can't fire alongside a button click).
  onOpenDetail?: (doc: Document) => void;
  subLayout: SubLayout;
  // Refresh the folder/document data after a folder mutation. Always
  // supplied so subfolder creation (allowed for staff / super admins who
  // are not customer admins) can refresh too.
  onFolderRefresh?: () => void;
  // True when the user may rename / delete folders (customer admins).
  canManageFolders?: boolean;
  // True when the user may upload into the folder they are viewing. Separate
  // from canManageFolders: suppliers upload documents but never rename or
  // delete folders, and overloading the folder-admin flag hid "Upload here"
  // from them entirely.
  canUpload?: boolean;
  // True when the user may create nested folders (customer users, staff,
  // super admins). Gates the "New subfolder" action; the backend
  // CanCreateSubfolder permission is the authoritative check.
  canCreateSubfolder?: boolean;
  // True when the user may decide which suppliers can see a folder
  // (customer admins). The backend IsHelixOrCustomerAdmin gate on the
  // assignment fields is the authoritative check.
  canAssignFolderAccess?: boolean;
  // Notifies the parent whether the user is currently drilled into a
  // folder. The page uses this to hide the global "Upload Document" button
  // while inside a folder — that button has no folder context, so showing
  // it next to the contextual "Upload here" reads as ambiguous.
  onFocusedFolderChange?: (focused: boolean) => void;
  // Multi-select for bulk permissions. When these are supplied the folder's
  // document list shows a checkbox per row and a selection bar with a
  // "Set permissions" action.
  selectedDocIds?: number[];
  onToggleDocSelected?: (id: number) => void;
  onSelectAllDocs?: (ids: number[]) => void;
  onClearSelectedDocs?: () => void;
  onOpenBulkPerms?: () => void;
  /** Archive every ticked document. */
  onBulkArchive?: () => void;
}

export function FolderView({
  categories,
  searchTerm,
  onDownload,
  onUploadOpen,
  onDelete,
  onEdit,
  onPreview,
  onOpenDetail,
  selectedDocIds,
  onToggleDocSelected,
  onSelectAllDocs,
  onClearSelectedDocs,
  onOpenBulkPerms,
  onBulkArchive,
  subLayout,
  onFolderRefresh,
  canManageFolders = false,
  canUpload = false,
  canCreateSubfolder = false,
  canAssignFolderAccess = false,
  onFocusedFolderChange,
}: FolderViewProps) {
  // Top-level pagination across root folders. 12 per page on desktop.
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryPageSize, setCategoryPageSize] = useState(FOLDER_GRID_PAGE_SIZE);

  // Drill-in navigation. A path of folder ids (root → … → current folder)
  // so arbitrarily-deep subfolders can be navigated with a breadcrumb.
  // Persisted in the URL (`?folder=4,43`) so a page refresh keeps you in
  // the folder you were viewing instead of resetting to the root grid.
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusedPath, setFocusedPath] = useState<Array<number | 'uncategorized'>>(() => {
    const raw = searchParams.get('folder');
    if (!raw) return [];
    return raw
      .split(',')
      .map((s) => (s === 'uncategorized' ? 'uncategorized' : Number(s)))
      .filter((x): x is number | 'uncategorized' => x === 'uncategorized' || Number.isFinite(x));
  });

  // Mirror the drill-in path into the URL (replace, so it doesn't pile up
  // history entries). Other query params (e.g. ?doc=) are preserved.
  useEffect(() => {
    const serialized = focusedPath.join(',');
    if ((searchParams.get('folder') ?? '') === serialized) return;
    const next = new URLSearchParams(searchParams);
    if (focusedPath.length) next.set('folder', serialized);
    else next.delete('folder');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedPath]);

  // Folder admin dialogs. Rename/delete are customer-admin only
  // (`onFolderRefresh` present); subfolder creation is gated separately by
  // `canCreateSubfolder`.
  const [renameTarget, setRenameTarget] = useState<CategoryWithDocuments | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CategoryWithDocuments | null>(null);
  const [subfolderTarget, setSubfolderTarget] = useState<CategoryWithDocuments | null>(null);
  const [accessTarget, setAccessTarget] = useState<CategoryWithDocuments | null>(null);

  // The top-level grid shows root folders only — subfolders surface when
  // their parent folder is opened.
  const rootCategories = useMemo(
    () => categories.filter((c) => c.parent == null),
    [categories],
  );

  // Child folders of a given category id (empty for the Uncategorized
  // pseudo-folder, which has no real id).
  const childFoldersOf = (parentId: number | null | undefined) =>
    typeof parentId === 'number'
      ? categories.filter((c) => c.parent === parentId)
      : [];

  const totalCategoryPages = Math.max(1, Math.ceil(rootCategories.length / categoryPageSize));
  const safeCategoryPage = Math.min(categoryPage, totalCategoryPages);
  const pagedCategories = useMemo(() => {
    const start = (safeCategoryPage - 1) * categoryPageSize;
    return rootCategories.slice(start, start + categoryPageSize);
  }, [rootCategories, safeCategoryPage, categoryPageSize]);

  const focusedId = focusedPath.length ? focusedPath[focusedPath.length - 1] : null;
  // Resolve the id back to the live category so a refetch (rename/delete/
  // create elsewhere) reflects immediately.
  const focusedCategory = focusedId !== null
    ? categories.find((c) => (c.id ?? 'uncategorized') === focusedId) ?? null
    : null;

  // Tell the page whether we're drilled into a folder so it can hide the
  // global "Upload Document" button (which has no folder context) in favour
  // of the contextual "Upload here". Reset to false on unmount so switching
  // to list view restores the global button.
  useEffect(() => {
    onFocusedFolderChange?.(focusedCategory != null);
    return () => onFocusedFolderChange?.(false);
  }, [focusedCategory, onFocusedFolderChange]);

  // Folder mutation dialogs — kept in one place so they render in both the
  // grid view and the drilled-in view.
  const folderDialogs = (
    <>
      {renameTarget && (
        <RenameFolderDialog
          category={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSaved={() => { setRenameTarget(null); onFolderRefresh?.(); }}
        />
      )}
      {deleteTarget && (
        <DeleteFolderDialog
          category={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); onFolderRefresh?.(); }}
        />
      )}
      {subfolderTarget && (
        <CreateSubfolderDialog
          parent={subfolderTarget}
          onClose={() => setSubfolderTarget(null)}
          onCreated={() => { setSubfolderTarget(null); onFolderRefresh?.(); }}
        />
      )}
      {accessTarget && (
        <FolderAccessDialog
          category={accessTarget}
          onClose={() => setAccessTarget(null)}
          onSaved={() => { setAccessTarget(null); onFolderRefresh?.(); }}
        />
      )}
    </>
  );

  // Per-folder action handlers passed to FolderTile menus. A handler is
  // only supplied when the user is entitled to that action.
  const folderActions = (category: CategoryWithDocuments) => ({
    onRename: canManageFolders && category.id ? () => setRenameTarget(category) : undefined,
    onDelete: canManageFolders && category.id ? () => setDeleteTarget(category) : undefined,
    onCreateSubfolder:
      canCreateSubfolder && category.id ? () => setSubfolderTarget(category) : undefined,
    onManageAccess:
      canAssignFolderAccess && category.id ? () => setAccessTarget(category) : undefined,
  });

  if (categories.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-center">
            <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No documents found matching your search' : 'No documents yet'}
            </p>
            <Button className="mt-4" onClick={() => onUploadOpen()}>
              <Plus className="h-4 w-4 mr-2" />
              Upload your first document
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (focusedCategory) {
    const crumbs = focusedPath.map((id) => ({
      id,
      name: categories.find((c) => (c.id ?? 'uncategorized') === id)?.name ?? 'Folder',
    }));
    return (
      <div className="space-y-4">
        {/* Breadcrumb — "All folders" plus one crumb per path segment.
            An "Upload here" action sits on the right so a user inside a
            folder/subfolder uploads straight into it (the upload dialog
            opens with this folder pre-selected) rather than scrolling back
            to the page-top button and re-finding the folder. Only real
            folders (numeric id) can be a target — the Uncategorised pseudo-
            folder has no id and falls back to the plain upload dialog. */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap text-sm">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFocusedPath([])}
              className="-ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              All folders
            </Button>
            {crumbs.map((c, i) => (
              <span key={`${c.id}`} className="flex items-center gap-1">
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                {i < crumbs.length - 1 ? (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground hover:underline"
                    onClick={() => setFocusedPath(focusedPath.slice(0, i + 1))}
                  >
                    {c.name}
                  </button>
                ) : (
                  <span className="font-medium">{c.name}</span>
                )}
              </span>
            ))}
          </div>
          {canUpload && (
            <Button
              size="sm"
              onClick={() =>
                onUploadOpen(
                  typeof focusedCategory.id === 'number' ? focusedCategory.id : undefined,
                )
              }
            >
              <Plus className="h-4 w-4 mr-2" />
              Upload here
            </Button>
          )}
        </div>
        <CategoryCard
          category={focusedCategory}
          childFolders={childFoldersOf(focusedCategory.id)}
          onOpenFolder={(id) => setFocusedPath([...focusedPath, id])}
          folderActions={folderActions}
          onDownload={onDownload}
          onDelete={onDelete}
          onEdit={onEdit}
          onPreview={onPreview}
          onOpenDetail={onOpenDetail}
          subLayout={subLayout}
          selectedDocIds={selectedDocIds}
          onToggleDocSelected={onToggleDocSelected}
          onSelectAllDocs={onSelectAllDocs}
          onClearSelectedDocs={onClearSelectedDocs}
          onOpenBulkPerms={onOpenBulkPerms}
          onBulkArchive={onBulkArchive}
        />
        {folderDialogs}
      </div>
    );
  }

  const categoryPager = totalCategoryPages > 1 ? (
    <Pagination
      currentPage={safeCategoryPage}
      totalPages={totalCategoryPages}
      totalCount={rootCategories.length}
      itemsPerPage={categoryPageSize}
      onPageChange={setCategoryPage}
      onPageSizeChange={(n) => { setCategoryPageSize(n); setCategoryPage(1); }}
      itemLabel="folders"
    />
  ) : null;

  return (
    <div className="space-y-4">
      {categoryPager}
      {subLayout === 'list' ? (
        <div className="divide-y rounded-md border bg-card">
          {pagedCategories.map((category) => (
            <FolderTile
              key={category.id ?? 'uncategorized'}
              category={category}
              variant="row"
              onOpen={() => setFocusedPath([category.id ?? 'uncategorized'])}
              {...folderActions(category)}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {pagedCategories.map((category) => (
            <FolderTile
              key={category.id ?? 'uncategorized'}
              category={category}
              onOpen={() => setFocusedPath([category.id ?? 'uncategorized'])}
              {...folderActions(category)}
            />
          ))}
        </div>
      )}
      {categoryPager}
      {folderDialogs}
    </div>
  );
}

interface FolderActionSet {
  onRename?: () => void;
  onDelete?: () => void;
  onCreateSubfolder?: () => void;
}

interface CategoryCardProps {
  category: CategoryWithDocuments;
  // Direct child folders of this category, rendered as tiles above the
  // document list.
  childFolders: CategoryWithDocuments[];
  // Navigate into a child folder (pushes onto the breadcrumb path).
  onOpenFolder: (id: number | 'uncategorized') => void;
  // Resolves the rename / delete / create-subfolder handlers for a folder,
  // each present only when the user is entitled to that action.
  folderActions: (category: CategoryWithDocuments) => FolderActionSet;
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
  subLayout: SubLayout;
  selectedDocIds?: number[];
  onToggleDocSelected?: (id: number) => void;
  onSelectAllDocs?: (ids: number[]) => void;
  onClearSelectedDocs?: () => void;
  onOpenBulkPerms?: () => void;
  /** Archive every ticked document. */
  onBulkArchive?: () => void;
}

function CategoryCard({
  category,
  childFolders,
  onOpenFolder,
  folderActions,
  onDownload,
  onDelete,
  onEdit,
  onPreview,
  onOpenDetail,
  subLayout,
  selectedDocIds,
  onToggleDocSelected,
  onSelectAllDocs,
  onClearSelectedDocs,
  onOpenBulkPerms,
  onBulkArchive,
}: CategoryCardProps) {
  // Auto-subgrouping is disabled: a folder with many documents used to split
  // into per-uploader "sub-folder" tiles, which read as subfolders being
  // created automatically after a multi-file upload. Always show the flat
  // (paginated) document list instead — folders are only ever created
  // explicitly via "New subfolder".
  const shouldSubgroup = false;

  // Group docs by uploading company_name. Supplier viewers get "Shared"
  // for any cross-supplier doc (the backend scrubs the name before it
  // gets here), so those docs all fall into one safe bucket.
  const subgroups = useMemo(() => {
    if (!shouldSubgroup) return [] as Array<[string, Document[]]>;
    const m: Record<string, Document[]> = {};
    for (const d of category.documents) {
      const key = d.company_name || '(Unassigned)';
      (m[key] = m[key] || []).push(d);
    }
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [category.documents, shouldSubgroup]);

  const [drillInto, setDrillInto] = useState<string | null>(null);
  const drillDocs = useMemo(
    () => (drillInto ? (subgroups.find(([n]) => n === drillInto)?.[1] ?? []) : []),
    [drillInto, subgroups],
  );

  // Pagination state for the currently-visible doc list (drill target if
  // sub-grouped, otherwise the flat category doc list).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DOCS_PER_PAGE);

  const visibleDocs = useMemo(
    () => (shouldSubgroup && !drillInto ? [] : (shouldSubgroup ? drillDocs : category.documents)),
    [shouldSubgroup, drillInto, drillDocs, category.documents],
  );
  // Sort controls for the document list (shared with the supplier Documents
  // tab): clickable Name / Date Uploaded headers; sorting runs over the full
  // visible set before pagination so it orders the whole folder, not one page.
  const { sortKey, sortDir, toggleSort, sorted: sortedDocs } = useDocumentSort(visibleDocs, () => setPage(1));
  const totalDocs = sortedDocs.length;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedDocs = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedDocs.slice(start, start + pageSize);
  }, [sortedDocs, safePage, pageSize]);

  const resetPaging = () => { setPage(1); };

  // "Select all" header checkbox state — covers every document in the folder
  // (all pages), so a bulk permission change can target the whole folder.
  const selectionEnabled = !!onToggleDocSelected;
  const folderDocIds = useMemo(() => sortedDocs.map((d) => d.id), [sortedDocs]);
  const selectedCount = useMemo(
    () => folderDocIds.filter((id) => selectedDocIds?.includes(id)).length,
    [folderDocIds, selectedDocIds],
  );
  const allSelected = folderDocIds.length > 0 && selectedCount === folderDocIds.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const toggleSelectAll = () => {
    if (allSelected) onClearSelectedDocs?.();
    else onSelectAllDocs?.(folderDocIds);
  };

  // When a folder holds BOTH subfolders and files, split them across two
  // tabs (Files | Folders) so neither buries the other. A folder with only
  // one of the two shows that content directly, no tabs.
  const [activeTab, setActiveTab] = useState<'files' | 'folders'>('files');
  const hasSubfolders = !drillInto && childFolders.length > 0;
  const hasFiles = sortedDocs.length > 0;
  const showTabs = hasSubfolders && hasFiles;
  const showSubfolders = hasSubfolders && (!showTabs || activeTab === 'folders');
  const showFiles = (showTabs && activeTab === 'files') || (!showTabs && !hasSubfolders);

  const pager = (!shouldSubgroup || drillInto) && totalPages > 1 ? (
    <Pagination
      currentPage={safePage}
      totalPages={totalPages}
      totalCount={totalDocs}
      itemsPerPage={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      itemLabel="documents"
    />
  ) : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {shouldSubgroup && drillInto && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => { setDrillInto(null); resetPaging(); }}
                aria-label="Back to folders"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div className={`p-2 rounded-lg ${getCategoryColor(category.color)}`}>
              {drillInto ? <Building2 className="h-5 w-5" /> : <Folder className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <CardTitle className="text-xl truncate">
                {drillInto ? drillInto : category.name}
              </CardTitle>
              {!drillInto && category.description && (
                <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
              )}
              {drillInto && (
                <p className="text-sm text-muted-foreground mt-1">
                  {category.name} › {drillDocs.length} {drillDocs.length === 1 ? 'document' : 'documents'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!drillInto && folderActions(category).onCreateSubfolder && (
              <Button
                variant="outline"
                size="sm"
                onClick={folderActions(category).onCreateSubfolder}
              >
                <FolderPlus className="h-4 w-4 mr-1.5" />
                New subfolder
              </Button>
            )}
            {shouldSubgroup && !drillInto && (
              <Badge variant="outline" className="text-xs">
                {subgroups.length} {subgroups.length === 1 ? 'folder' : 'folders'}
              </Badge>
            )}
            <Badge variant="secondary">
              {drillInto
                ? `${drillDocs.length} ${drillDocs.length === 1 ? 'document' : 'documents'}`
                : `${category.document_count} ${category.document_count === 1 ? 'document' : 'documents'}`}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Files / Folders tabs — only when the folder holds both. */}
        {showTabs && (
          <div className="flex items-center gap-1 border-b">
            <button
              type="button"
              onClick={() => setActiveTab('files')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'files'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Files ({sortedDocs.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('folders')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'folders'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Folders ({childFolders.length})
            </button>
          </div>
        )}
        {/* Nested subfolders — shown when there are no files (no tabs), or when
            the Folders tab is active. Hidden in the uploader-subgroup view. */}
        {showSubfolders && (
          <div className="space-y-2">
            {!showTabs && (
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Subfolders
              </p>
            )}
            {/* Honour the tile/list toggle for nested subfolders too — the
                drilled-in view previously hard-coded a tile grid, so the
                List layout button did nothing once inside a folder. */}
            {subLayout === 'list' ? (
              <div className="divide-y rounded-md border bg-card">
                {childFolders.map((child) => (
                  <FolderTile
                    key={child.id ?? 'uncategorized'}
                    category={child}
                    variant="row"
                    onOpen={() => onOpenFolder(child.id ?? 'uncategorized')}
                    {...folderActions(child)}
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {childFolders.map((child) => (
                  <FolderTile
                    key={child.id ?? 'uncategorized'}
                    category={child}
                    onOpen={() => onOpenFolder(child.id ?? 'uncategorized')}
                    {...folderActions(child)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {showFiles && (shouldSubgroup && !drillInto ? (
          subLayout === 'tiles' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {subgroups.map(([label, docs]) => (
                <SupplierTile
                  key={label}
                  label={label}
                  count={docs.length}
                  onOpen={() => { setDrillInto(label); resetPaging(); }}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {subgroups.map(([label, docs]) => (
                <SupplierRow
                  key={label}
                  label={label}
                  documents={docs}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onOpenDetail={onOpenDetail}
                  docLayout={subLayout}
                />
              ))}
            </div>
          )
        ) : (
          <>
            {/* Multi-select action bar — only when at least one doc is ticked.
                Lets the user set permissions on the whole selection at once. */}
            {onToggleDocSelected && (selectedDocIds?.length ?? 0) > 0 && (
              <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg border bg-primary/5">
                <span className="text-sm font-medium">
                  {selectedDocIds?.length} selected
                </span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => onClearSelectedDocs?.()}>
                    Clear
                  </Button>
                  {onBulkArchive && (
                    <Button size="sm" variant="outline" onClick={() => onBulkArchive()} data-testid="documents-bulk-archive">
                      <Archive className="h-4 w-4 mr-2" />
                      Archive selected
                    </Button>
                  )}
                  <Button size="sm" onClick={() => onOpenBulkPerms?.()}>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Set permissions
                  </Button>
                </div>
              </div>
            )}
            {pager}
            {subLayout === 'list' ? (
              <div className="space-y-1">
                <DocumentSortHeader
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  selectable={selectionEnabled}
                  allSelected={allSelected}
                  someSelected={someSelected}
                  onToggleSelectAll={toggleSelectAll}
                />
                <DocumentRowList
                  documents={pagedDocs}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onOpenDetail={onOpenDetail}
                  selectedDocIds={selectedDocIds}
                  onToggleDocSelected={onToggleDocSelected}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pagedDocs.map((doc) => (
                  <DocumentTile
                    key={doc.id}
                    doc={doc}
                    onDownload={onDownload}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onPreview={onPreview}
                    onOpenDetail={onOpenDetail}
                  />
                ))}
              </div>
            )}
            {pager}
          </>
        ))}
      </CardContent>
    </Card>
  );
}

interface SupplierTileProps {
  label: string;
  count: number;
  onOpen: () => void;
}

function SupplierTile({ label, count, onOpen }: SupplierTileProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title={label}
      className="group flex flex-col rounded-lg border bg-card p-3 text-left transition-all duration-150 ease-out hover:border-primary/50 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="flex items-center justify-between">
        <Folder className="h-8 w-8 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.4} />
        <span className="text-[11px] font-semibold tabular-nums text-muted-foreground group-hover:text-foreground">
          {count}
        </span>
      </div>
      <p className="mt-3 text-[13px] font-medium leading-snug line-clamp-2 break-words">
        {label}
      </p>
    </button>
  );
}

interface SupplierRowProps {
  label: string;
  documents: Document[];
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
  docLayout: SubLayout;
}

function SupplierRow({ label, documents, onDownload, onDelete, onEdit, onPreview, onOpenDetail, docLayout }: SupplierRowProps) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded
            ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{label}</span>
        </div>
        <Badge variant="secondary" className="shrink-0">{documents.length}</Badge>
      </button>
      {expanded && (
        docLayout === 'list' ? (
          <div className="px-3 pb-3 pt-1 bg-muted/20">
            <DocumentRowList
              documents={documents}
              onDownload={onDownload}
              onDelete={onDelete}
              onEdit={onEdit}
              onPreview={onPreview}
              onOpenDetail={onOpenDetail}
            />
          </div>
        ) : (
          <div className="px-3 pb-3 pt-1 bg-muted/20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {documents.map((doc) => (
              <DocumentTile
                key={doc.id}
                doc={doc}
                onDownload={onDownload}
                onDelete={onDelete}
                onEdit={onEdit}
                onPreview={onPreview}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

interface DocumentTileProps {
  doc: Document;
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
}

function DocumentTile({ doc, onDownload, onDelete, onEdit, onPreview, onOpenDetail }: DocumentTileProps) {
  const cleanDescription = stripImportMetadata(doc.description);
  // CR-5330: Tile body opens the details popup; the kebab menu still
  // exposes quick actions. stopPropagation() on the menu trigger keeps
  // the row click from firing alongside a menu item click.
  const handleBodyClick = () => onOpenDetail?.(doc);
  const handleBodyKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDetail?.(doc);
    }
  };
  return (
    <Card className="group hover:shadow-md transition-shadow h-full">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div
            className={`flex items-start gap-3 flex-1 min-w-0 ${onOpenDetail ? 'cursor-pointer' : ''}`}
            onClick={onOpenDetail ? handleBodyClick : undefined}
            onKeyDown={onOpenDetail ? handleBodyKey : undefined}
            role={onOpenDetail ? 'button' : undefined}
            tabIndex={onOpenDetail ? 0 : undefined}
          >
            <div className="text-muted-foreground mt-0.5 shrink-0">{getFileIcon(doc)}</div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[13px] leading-snug line-clamp-2 break-words" title={doc.title}>
                {doc.title}
              </h3>
              {cleanDescription && (
                <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                  {cleanDescription}
                </p>
              )}
            </div>
          </div>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 -mt-1 opacity-60 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onDownload(doc)}>
                <Download className="h-4 w-4 mr-2" />Download
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onPreview?.(doc)}>
                <Eye className="h-4 w-4 mr-2" />Preview
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(doc)}>
                  <Edit className="h-4 w-4 mr-2" />Edit
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem className="text-red-600" onClick={() => onDelete(doc)}>
                  <Trash2 className="h-4 w-4 mr-2" />Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-3 border-t gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <User className="h-3 w-3 shrink-0" />
            <span className="truncate">{doc.uploaded_by_name || '—'}</span>
          </div>
          {doc.formatted_file_size && (
            <span className="shrink-0 tabular-nums">{doc.formatted_file_size}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 mt-2">
          {doc.is_public && (
            <Badge variant="outline" className="text-[10px]">Public</Badge>
          )}
          {/* CR-5329 */}
          {doc.requires_acceptance && (
            <Badge variant="default" className="text-[10px] bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200">
              Requires acceptance
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function stripImportMetadata(description?: string): string {
  if (!description) return '';
  const clean = description
    .replace(/\[mab-doc#[^\]]+\]/gi, '')
    .replace(/Original\s*FileName\s*:[^|]*?(?=SourceID|$)/i, '')
    .replace(/SourceID\s*:\s*\S+/i, '')
    .trim();
  return clean.length > 3 ? clean : '';
}
